/**
 * The shift slip — what prints on the roll when a shift closes.
 *
 * One component for two places: the cashier's close screen, and the Reports
 * tab, where any past shift can be printed again. The reprint is this exact
 * format, so a slip pulled a month later lines up with the one in the drawer —
 * only the figures may differ, where bills have since been refiled onto the
 * shift.
 *
 * Marked .bill, so the shared print rule makes it, and only it, the thing that
 * reaches the paper; .shift-report flattens its themed colours for the
 * thermal head. Buttons and advice belong outside it.
 */
import { formatPKR, formatServerTime } from '@foodrun/shared';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * A YYYY-MM-DD as a person reads it: "12 Sep 2026". Built by hand rather than
 * through toLocaleDateString so the printed slip reads the same on every till.
 */
function niceDate(iso) {
  const [y, m, d] = String(iso ?? '').split('-').map(Number);
  if (!y || !m || !d) return iso || '';
  return `${String(d).padStart(2, '0')} ${MONTHS[m - 1]} ${y}`;
}

/**
 * `result` is what /shifts/close (or /reports/shifts/:id) returns: a
 * shiftNumber and the figures under `summary`.
 */
export function ShiftReport({ result }) {
  // The server returns the shift's figures under `summary`; reading them off
  // the top level (as this once did) showed zeros for everything. The fallback
  // keeps older callers working.
  const s = result.summary ?? result;
  // A shift still open has no count yet, so nothing to compare.
  const counted = s.actualCash != null;
  const variance = Number(s.variance ?? 0);
  const over = variance > 0;
  const exact = Math.abs(variance) < 0.01;

  return (
    <div className="bill shift-report">
      <h1 className="font-semibold text-xl text-ink">{counted ? 'Shift closed' : 'Shift so far'}</h1>
      {/* Which shift it was — morning or evening — named on the report so a
          stack of them can be told apart at a glance. */}
      {s.shiftName && (
        <p className="mt-0.5 text-sm font-semibold text-ink capitalize">{s.shiftName} shift</p>
      )}
      {result.shiftNumber && (
        <p className="mt-0.5 text-sm text-ink-3">{result.shiftNumber}</p>
      )}
      {/* The trading day this shift belonged to — the shop's manually-set
          business day, printed on the slip so a stack of them can be filed by
          the day they were traded, not the clock date they were closed on. */}
      {s.businessDate && (
        <p className="mt-0.5 text-sm text-ink-3">{niceDate(s.businessDate)}</p>
      )}

      {/* Whose shift, and the hours it ran — so a slip picked up later says
          who to ask and what it covers. Each line only when the server sent
          it, so an older server still prints a clean report. */}
      {(s.cashierName || s.openedAt || s.closedAt) && (
        <dl className="mt-3 space-y-1 text-left">
          {s.cashierName && <Row label="Cashier" value={s.cashierName} />}
          {s.openedAt && <Row label="Opened" value={formatServerTime(s.openedAt)} />}
          {s.closedAt && <Row label="Closed" value={formatServerTime(s.closedAt)} />}
        </dl>
      )}

      {/* What the shift took — the sales report the cashier closes on. */}
      <p className="mt-4 mb-1.5 text-left text-xs font-medium uppercase tracking-wide text-ink-3">
        This shift
      </p>
      <dl className="space-y-2 rounded-xl border border-rule bg-surface p-4 text-left">
        <Row label="Orders" value={s.totalOrders ?? 0} />
        {/* The day's bill serials this shift rang up — "#12 to #47". */}
        {s.bills?.first != null && (
          <Row
            label="Bills"
            value={
              s.bills.first === s.bills.last
                ? `#${s.bills.first}`
                : `#${s.bills.first} – #${s.bills.last}`
            }
          />
        )}
        <Row label="Sales" value={formatPKR(s.totalSales ?? 0)} />
        {/* Delivery charged this shift, and the sale with it taken back off —
            the food takings on their own, which is what most owners want. */}
        {Number(s.deliveryCharges) > 0 && (
          <>
            <Row label="Delivery charges" value={formatPKR(s.deliveryCharges)} />
            <Row
              label="Sales without delivery"
              value={formatPKR(s.salesWithoutDelivery ?? (s.totalSales ?? 0) - (s.deliveryCharges ?? 0))}
            />
          </>
        )}
        {/* What was given away in discounts across the shift. */}
        {Number(s.totalDiscounts) > 0 && (
          <Row label="Discounts" value={formatPKR(s.totalDiscounts)} />
        )}
        {/* Cash with the refunds, delivery charges and discounts already taken
            off — the cash actually in hand, not the cash rung up. Falls back to
            working it out for a server that does not send the figure yet. */}
        <Row
          label="Cash"
          value={formatPKR(
            s.cashInHand ??
              (s.cashSales ?? 0) -
                (s.refunds ?? 0) -
                (s.deliveryCharges ?? 0) -
                (s.totalDiscounts ?? 0)
          )}
        />
        {/* Card and online on their own lines: one is checked against the card
            machine, the other against the bank app. */}
        <Row label="Card" value={formatPKR(s.cardSales ?? 0)} />
        <Row label="Online" value={formatPKR(s.onlineSales ?? 0)} />
        {/* Sold on a staff account — in Sales, but no money taken, so it is
            named here and the figures add up: Sales = Cash + Card + Account + Foodpanda. */}
        {Number(s.accountSales) > 0 && (
          <Row label="On account" value={formatPKR(s.accountSales)} />
        )}
        {/* Rung up on Foodpanda — a sale, but owed by Foodpanda, not taken here. */}
        {Number(s.foodpandaSales) > 0 && (
          <Row label="Foodpanda" value={formatPKR(s.foodpandaSales)} />
        )}
        {/* Only what stayed refunded: a bill refunded and rung up again is one
            ordinary sale and is not counted here. That is what lets the slip be
            checked by hand — Sales without delivery, less Discounts, Card,
            Online, On account, Foodpanda and Refunds, comes to Cash. */}
        {Number(s.refunds) > 0 && <Row label="Refunds" value={formatPKR(s.refunds)} />}
        {Number(s.voidOrders) > 0 && <Row label="Voided" value={s.voidOrders} />}
      </dl>

      {/* Staff who paid towards their account while the shift was open. Two
          short lines a person — the payment, then what they owed and what is
          left — so a few of them do not turn the slip into a ledger. Not shown
          at all on a shift nobody paid in. */}
      {(s.staffPayments ?? []).length > 0 && (
        <>
          <p className="mt-4 mb-1.5 text-left text-xs font-medium uppercase tracking-wide text-ink-3">
            Staff account payments
          </p>
          <dl className="space-y-2 rounded-xl border border-rule bg-surface p-4 text-left">
            {s.staffPayments.map((p) => (
              <div key={p.id ?? p.name}>
                <div className="flex items-baseline justify-between gap-2">
                  <dt className="min-w-0 truncate text-sm text-ink">{p.name}</dt>
                  <dd className="shrink-0 tabular-nums text-ink">Paid {formatPKR(p.paid)}</dd>
                </div>
                <p className="text-xs tabular-nums text-ink-3">
                  Owed {formatPKR(p.owed)} · Left {formatPKR(p.left)}
                </p>
              </div>
            ))}
            {s.staffPayments.length > 1 && (
              <div className="flex items-baseline justify-between border-t border-rule pt-2">
                <dt className="text-sm text-ink-2">Paid in total</dt>
                <dd className="tabular-nums text-ink">{formatPKR(s.staffPaymentsTotal ?? 0)}</dd>
              </div>
            )}
          </dl>
        </>
      )}

      {/* The drawer, counted against what it should hold. */}
      <p className="mt-4 mb-1.5 text-left text-xs font-medium uppercase tracking-wide text-ink-3">
        The drawer
      </p>
      <dl className="space-y-2 rounded-xl border border-rule bg-surface p-4 text-left">
        <Row label="Opened with" value={formatPKR(s.openingAmount ?? 0)} />
        {/* What staff paid towards their accounts went into this drawer, so it
            is named here: Opened with + Cash + this = Expected. Without the
            line, Expected would be higher than the float and Cash explain. */}
        {Number(s.staffPaymentsTotal) > 0 && (
          <Row label="Staff account payments" value={formatPKR(s.staffPaymentsTotal)} />
        )}
        <Row label="Expected in the drawer" value={formatPKR(s.expectedCash ?? 0)} />
        {counted ? (
          <>
            <Row label="You counted" value={formatPKR(s.actualCash ?? 0)} />

            <div
              className={`flex items-baseline justify-between rounded-lg px-3 py-2.5 ${
                exact ? 'bg-good-bg' : over ? 'bg-warn-bg' : 'bg-bad-bg'
              }`}
            >
              <dt
                className={`text-sm font-medium ${exact ? 'text-good' : over ? 'text-warn' : 'text-bad'}`}
              >
                {exact ? 'Balanced' : over ? 'Over by' : 'Short by'}
              </dt>
              <dd
                className={`text-xl font-semibold tabular-nums ${
                  exact ? 'text-good' : over ? 'text-warn' : 'text-bad'
                }`}
              >
                {exact ? '—' : formatPKR(Math.abs(variance))}
              </dd>
            </div>
          </>
        ) : (
          <Row label="You counted" value="not yet — still open" />
        )}
      </dl>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline justify-between">
      <dt className="text-sm text-ink-2">{label}</dt>
      <dd className="tabular-nums text-ink">{value}</dd>
    </div>
  );
}
